import { z } from "zod";
import { appIdSchema, isoTimestampSchema, runIdSchema, userIdSchema, } from "./identifiers.js";
export const providerConnectionStatusSchema = z.enum([
    "pending",
    "connected",
    "expired",
    "error",
    "revoked",
]);
export const providerAuthMethodSchema = z.enum([
    "browser_oauth",
    "device_oauth",
    "manual_oauth",
    "api_key",
]);
export const providerAccountEmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(320)
    .pipe(z.email());
export const providerAuthMethodDescriptorSchema = z
    .object({
    method: providerAuthMethodSchema,
    label: z.string().trim().min(1).max(200),
    available: z.boolean(),
    unavailableReason: z.string().trim().min(1).max(500).nullable(),
})
    .strict();
export const providerDescriptorSchema = z
    .object({
    schemaVersion: z.literal(1),
    id: z.string().trim().min(1).max(100),
    displayName: z.string().trim().min(1).max(200),
    opencodeProviderId: z.string().trim().min(1).max(100),
    defaultModel: z.string().trim().min(1).max(200).nullable().optional(),
    authMethods: z.array(providerAuthMethodDescriptorSchema).min(1).max(10),
})
    .strict();
const providerCreditNanodollarsSchema = z.string().regex(/^(0|[1-9][0-9]{0,39})$/);
/** USD nanodollars a provider reports for one key; null remaining means no cap. */
export const providerConnectionCreditSchema = z
    .object({
    remainingNanodollars: providerCreditNanodollarsSchema.nullable(),
    limitNanodollars: providerCreditNanodollarsSchema.nullable(),
    reportedAt: isoTimestampSchema,
})
    .strict();
export const providerConnectionViewSchema = z
    .object({
    schemaVersion: z.literal(1),
    id: z.uuid(),
    provider: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(200),
    authMethod: providerAuthMethodSchema,
    status: providerConnectionStatusSchema,
    stateVersion: z.number().int().positive(),
    expiresAt: isoTimestampSchema.nullable(),
    errorClass: z.string().trim().min(1).max(200).nullable(),
    accountHint: z.string().trim().min(1).max(320).nullable().optional(),
    isDefault: z.boolean().optional(),
    connectedAt: isoTimestampSchema.nullable().optional(),
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    revokedAt: isoTimestampSchema.nullable(),
    /** Credit the provider reports for the key (OpenRouter). A null limit has no cap. */
    credit: providerConnectionCreditSchema.nullable().optional(),
    /** OpenRouter: route only to providers that don't train on or keep prompts. */
    privateProvidersOnly: z.boolean().optional(),
})
    .strict();
const providerAuthorizationStartBase = {
    schemaVersion: z.literal(1),
    attemptId: z.uuid(),
    connection: providerConnectionViewSchema,
    expiresAt: isoTimestampSchema,
};
export const providerAuthorizationStartSchema = z.discriminatedUnion("method", [
    z
        .object({
        ...providerAuthorizationStartBase,
        method: z.literal("browser_oauth"),
        authorizationUrl: z.url(),
    })
        .strict(),
    z
        .object({
        ...providerAuthorizationStartBase,
        method: z.literal("device_oauth"),
        authorizationUrl: z.url(),
        userCode: z.string().trim().min(2).max(100),
        instructions: z.string().trim().min(1).max(1_000),
    })
        .strict(),
    z
        .object({
        ...providerAuthorizationStartBase,
        method: z.literal("manual_oauth"),
        authorizationUrl: z.url(),
        instructions: z.string().trim().min(1).max(1_000),
    })
        .strict(),
]);
export const providerAuthorizationStatusSchema = z.enum([
    "pending",
    "exchanging",
    "completed",
    "denied",
    "failed",
    "expired",
    "cancelled",
]);
export const providerAuthorizationViewSchema = z.discriminatedUnion("method", [
    z
        .object({
        schemaVersion: z.literal(1),
        attemptId: z.uuid(),
        connection: providerConnectionViewSchema,
        method: z.literal("browser_oauth"),
        status: providerAuthorizationStatusSchema,
        // The browser authorization URL is returned exactly once by the start
        // response. Poll/read views must not retain or re-expose its PKCE state.
        authorizationUrl: z.null(),
        expiresAt: isoTimestampSchema,
        errorClass: z.string().trim().min(1).max(200).nullable(),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal(1),
        attemptId: z.uuid(),
        connection: providerConnectionViewSchema,
        method: z.literal("device_oauth"),
        status: providerAuthorizationStatusSchema,
        authorizationUrl: z.url().nullable(),
        userCode: z.string().trim().min(2).max(100).nullable(),
        instructions: z.string().trim().min(1).max(1_000).nullable(),
        expiresAt: isoTimestampSchema,
        errorClass: z.string().trim().min(1).max(200).nullable(),
    })
        .strict()
        .superRefine((value, context) => {
        const challengeFields = [
            ["authorizationUrl", value.authorizationUrl],
            ["userCode", value.userCode],
            ["instructions", value.instructions],
        ];
        for (const [field, fieldValue] of challengeFields) {
            if (value.status === "pending"
                ? fieldValue === null
                : fieldValue !== null) {
                context.addIssue({
                    code: "custom",
                    path: [field],
                    message: value.status === "pending"
                        ? "pending device authorization requires its challenge"
                        : "device challenge is available only while authorization is pending",
                });
            }
        }
    }),
    z
        .object({
        schemaVersion: z.literal(1),
        attemptId: z.uuid(),
        connection: providerConnectionViewSchema,
        method: z.literal("manual_oauth"),
        status: providerAuthorizationStatusSchema,
        authorizationUrl: z.url().nullable(),
        instructions: z.string().trim().min(1).max(1_000).nullable(),
        expiresAt: isoTimestampSchema,
        errorClass: z.string().trim().min(1).max(200).nullable(),
    })
        .strict()
        .superRefine((value, context) => {
        const challengeFields = [
            ["authorizationUrl", value.authorizationUrl],
            ["instructions", value.instructions],
        ];
        for (const [field, fieldValue] of challengeFields) {
            if (value.status === "pending"
                ? fieldValue === null
                : fieldValue !== null) {
                context.addIssue({
                    code: "custom",
                    path: [field],
                    message: value.status === "pending"
                        ? "pending manual authorization requires its challenge"
                        : "manual challenge is available only while authorization is pending",
                });
            }
        }
    }),
]);
export const providerConnectionCreateSchema = z.discriminatedUnion("authMethod", [
    z
        .object({
        authMethod: z.literal("browser_oauth"),
        label: z.string().trim().min(1).max(200).optional(),
        reconnectConnectionId: z.uuid().optional(),
    })
        .strict(),
    z
        .object({
        authMethod: z.literal("device_oauth"),
        label: z.string().trim().min(1).max(200).optional(),
        reconnectConnectionId: z.uuid().optional(),
    })
        .strict(),
    z
        .object({
        authMethod: z.literal("manual_oauth"),
        label: z.string().trim().min(1).max(200).optional(),
        reconnectConnectionId: z.uuid().optional(),
    })
        .strict(),
    z
        .object({
        authMethod: z.literal("api_key"),
        label: z.string().trim().min(1).max(200).optional(),
        apiKey: z.string().trim().min(8).max(100_000),
        reconnectConnectionId: z.uuid().optional(),
    })
        .strict(),
]);
export const providerConnectionUpdateSchema = z
    .object({
    label: z.string().trim().min(1).max(200).optional(),
    makeDefault: z.literal(true).optional(),
    /** OpenRouter connections only. */
    privateProvidersOnly: z.boolean().optional(),
})
    .strict()
    .refine((value) => value.label !== undefined ||
    value.makeDefault === true ||
    value.privateProvidersOnly !== undefined, { message: "At least one provider connection setting is required" });
export const providerAuthJobLaunchSchema = z
    .object({
    schemaVersion: z.literal(1),
    attemptId: z.uuid(),
    userId: userIdSchema,
    provider: z.string().trim().min(1).max(100),
    opencodeProviderId: z.string().trim().min(1).max(100),
    method: z.enum(["device_oauth", "manual_oauth"]),
    methodLabel: z.string().trim().min(1).max(200),
    callbackUrl: z.url(),
    callbackToken: z.string().min(32).max(4_096),
    expiresAt: isoTimestampSchema,
})
    .strict();
export const providerAuthJobStateSchema = z.enum([
    "starting",
    "waiting",
    "completed",
    "failed",
    "cancelled",
    "removed",
]);
export const providerAuthJobHandleSchema = z
    .object({
    schemaVersion: z.literal(1),
    attemptId: z.uuid(),
    userId: userIdSchema,
    provider: z.string().trim().min(1).max(100),
    method: z.enum(["device_oauth", "manual_oauth"]),
    state: providerAuthJobStateSchema,
    containerName: z.string().min(1).max(128),
    authorizationUrl: z.url().nullable(),
    userCode: z.string().trim().min(2).max(100).nullable(),
    instructions: z.string().trim().min(1).max(1_000).nullable(),
    expiresAt: isoTimestampSchema,
    errorClass: z.string().trim().min(1).max(200).nullable(),
    retryable: z.boolean(),
})
    .strict();
/** Sensitive one-time authorization code relayed to a provider-auth runtime. */
export const providerAuthCodeSubmissionSchema = z
    .object({
    schemaVersion: z.literal(1),
    attemptId: z.uuid(),
    authorizationCode: z.string().trim().min(3).max(10_000),
})
    .strict();
/** Internal relay envelope; never expose the worker control token publicly. */
export const providerAuthCodeRelaySchema = providerAuthCodeSubmissionSchema.extend({
    controlToken: z.string().min(32).max(4_096),
}).strict();
const credentialBase = {
    schemaVersion: z.literal(1),
    provider: z.string().trim().min(1).max(100),
    opencodeProviderId: z.string().trim().min(1).max(100),
};
/**
 * Sensitive canonical provider state. Values matching this schema must only
 * cross the encrypted vault/runtime-private provider boundary. They are not a
 * runtime event, conversation payload, log field, or public API response.
 */
export const providerCredentialStateSchema = z.discriminatedUnion("kind", [
    z
        .object({
        ...credentialBase,
        kind: z.literal("oauth"),
        accessToken: z.string().min(1).max(100_000),
        /** Required to re-materialize a ChatGPT/Codex file login. */
        idToken: z.string().min(1).max(100_000).nullable().optional(),
        refreshToken: z.string().min(1).max(100_000).nullable(),
        expiresAt: isoTimestampSchema,
        scopes: z.array(z.string().trim().min(1).max(200)).max(100),
        accountId: z.string().trim().min(1).max(500).nullable(),
        accountEmail: providerAccountEmailSchema.nullable().optional(),
    })
        .strict(),
    z
        .object({
        ...credentialBase,
        kind: z.literal("api_key"),
        apiKey: z.string().min(1).max(100_000),
    })
        .strict(),
]);
/** Sensitive completion posted from a one-shot provider-auth runtime. */
export const providerAuthRuntimeUpdateSchema = z.discriminatedUnion("outcome", [
    z
        .object({
        schemaVersion: z.literal(1),
        attemptId: z.uuid(),
        userId: userIdSchema,
        provider: z.string().trim().min(1).max(100),
        outcome: z.literal("completed"),
        state: providerCredentialStateSchema,
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal(1),
        attemptId: z.uuid(),
        userId: userIdSchema,
        provider: z.string().trim().min(1).max(100),
        outcome: z.literal("failed"),
        errorClass: z.string().trim().min(1).max(200),
        retryable: z.boolean(),
    })
        .strict(),
]);
/** Non-secret manifest placed beside an engine-specific private auth file. */
export const runtimeProviderMaterialSchema = z
    .object({
    schemaVersion: z.literal(1),
    connectionId: z.uuid(),
    provider: z.string().trim().min(1).max(100),
    stateVersion: z.number().int().positive(),
    opencodeProviderId: z.string().trim().min(1).max(100),
    authFile: z.enum([
        "opencode-auth.json",
        "claude-credentials.json",
        "codex-auth.json",
        "openrouter-auth.json",
    ]),
    accessMode: z.enum(["embedded", "brokered"]).optional(),
    expiresAt: isoTimestampSchema.nullable(),
    scopes: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
})
    .strict()
    .refine((value) => value.accessMode !== "brokered" ||
    (value.provider === "openai" &&
        (value.authFile === "codex-auth.json" ||
            value.authFile === "opencode-auth.json")) ||
    (value.provider === "anthropic" &&
        value.authFile === "claude-credentials.json"), {
    path: ["accessMode"],
    message: "brokered provider material requires a compatible OpenAI or Claude adapter",
})
    .refine((value) => (value.provider === "openrouter") ===
    (value.authFile === "openrouter-auth.json"), {
    path: ["authFile"],
    message: "OpenRouter material uses exactly the OpenRouter key file",
});
/**
 * Sensitive `openrouter-auth.json` content for the OpenRouter Codex harness.
 * The runtime hands the key only to the native Codex process environment.
 */
export const runtimeOpenRouterAuthSchema = z
    .object({
    apiKey: z.string().trim().min(8).max(100_000),
})
    .strict();
/** Access-token-only bootstrap material for a brokered OpenAI runtime. */
export const runtimeOpenAiBrokerAuthSchema = z
    .object({
    schemaVersion: z.literal(1),
    type: z.literal("chatgptAuthTokens"),
    connectionId: z.uuid(),
    stateVersion: z.number().int().positive(),
    accessToken: z.string().min(16).max(100_000),
    chatgptAccountId: z.string().trim().min(1).max(500),
    chatgptPlanType: z.string().trim().min(1).max(100).nullable(),
    expiresAt: isoTimestampSchema,
})
    .strict();
/** Compatibility export for the original native Codex broker contract. */
export const runtimeCodexBrokerAuthSchema = runtimeOpenAiBrokerAuthSchema;
/**
 * Access-token-only bootstrap material for a brokered Claude runtime. It
 * never contains the refresh token, which stays in the provider vault.
 */
export const runtimeClaudeBrokerAuthSchema = z
    .object({
    schemaVersion: z.literal(1),
    type: z.literal("claudeAiOauthAccessToken"),
    connectionId: z.uuid(),
    stateVersion: z.number().int().positive(),
    accessToken: z.string().min(16).max(100_000),
    expiresAt: isoTimestampSchema,
})
    .strict();
/** Run-authenticated request for the newest broker-owned access token. */
export const runtimeProviderAccessRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    connectionId: z.uuid(),
    expectedStateVersion: z.number().int().positive(),
    reason: z.enum(["initial", "unauthorized"]),
})
    .strict();
/**
 * Sensitive OpenAI response. Its shape predates the provider discriminator and
 * stays unchanged so existing Codex and OpenCode runtimes keep parsing it.
 */
export const runtimeOpenAiProviderAccessResponseSchema = z
    .object({
    schemaVersion: z.literal(1),
    connectionId: z.uuid(),
    stateVersion: z.number().int().positive(),
    accessToken: z.string().min(16).max(100_000),
    chatgptAccountId: z.string().trim().min(1).max(500),
    chatgptPlanType: z.string().trim().min(1).max(100).nullable(),
    expiresAt: isoTimestampSchema,
})
    .strict();
/** Sensitive Claude subscription response with an explicit discriminator. */
export const runtimeClaudeProviderAccessResponseSchema = z
    .object({
    schemaVersion: z.literal(1),
    provider: z.literal("anthropic"),
    connectionId: z.uuid(),
    stateVersion: z.number().int().positive(),
    accessToken: z.string().min(16).max(100_000),
    expiresAt: isoTimestampSchema,
})
    .strict();
/** Sensitive response. It contains no refresh or identity token. */
export const runtimeProviderAccessResponseSchema = z.union([
    runtimeOpenAiProviderAccessResponseSchema,
    runtimeClaudeProviderAccessResponseSchema,
]);
export function isClaudeProviderAccess(value) {
    return "provider" in value && value.provider === "anthropic";
}
export function isOpenAiProviderAccess(value) {
    return !isClaudeProviderAccess(value);
}
/**
 * Sensitive, run-authenticated callback used only to reconcile a credential
 * refreshed by the engine. It deliberately is not part of RuntimeEvent.
 */
export const runtimeProviderStateUpdateSchema = z
    .object({
    schemaVersion: z.literal(1),
    runId: runIdSchema,
    userId: userIdSchema,
    appId: appIdSchema,
    connectionId: z.uuid(),
    expectedStateVersion: z.number().int().positive(),
    state: providerCredentialStateSchema,
})
    .strict();
//# sourceMappingURL=providers.js.map